import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Form, Table, Spinner, Alert, Button } from 'react-bootstrap'
import client from '../../api/client'
import UsageChart from '../../components/charts/UsageChart'

function Usage() {
  const [usageData, setUsageData] = useState([])
  const [topModels, setTopModels] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({
    dateFrom: getDefaultDateFrom(),
    dateTo: getDefaultDateTo(),
    userId: '',
    modelId: '',
    groupBy: 'day',
  })

  function getDefaultDateFrom() {
    const date = new Date()
    date.setDate(date.getDate() - 30)
    return date.toISOString().split('T')[0]
  }

  function getDefaultDateTo() {
    return new Date().toISOString().split('T')[0]
  }

  useEffect(() => {
    fetchUsageData()
  }, [])

  const fetchUsageData = async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (filters.dateFrom) params.append('date_from', filters.dateFrom)
      if (filters.dateTo) params.append('date_to', filters.dateTo)
      if (filters.userId) params.append('user_id', filters.userId)
      if (filters.modelId) params.append('model_id', filters.modelId)
      params.append('group_by', filters.groupBy)

      const [usageResponse, topModelsResponse] = await Promise.all([
        client.get(`/admin/api/v1/analytics/usage?${params}`),
        client.get('/admin/api/v1/analytics/models/top/'),
      ])

      setUsageData(usageResponse.data.data || [])
      setTopModels(topModelsResponse.data.models || [])
    } catch (err) {
      console.error('Error fetching usage data:', err)
      setError('Erreur lors du chargement des données d\'utilisation')
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const handleApplyFilters = () => {
    fetchUsageData()
  }

  const totalRequests = usageData.reduce((sum, item) => sum + (item.count || 0), 0)
  const totalTokens = usageData.reduce((sum, item) => sum + (item.tokens || 0), 0)

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <h1 className="h3">Analyse d'utilisation</h1>
          <p className="text-muted">
            Visualisez les statistiques d'utilisation des modèles LLM
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Header>Filtres</Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Date de début</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Date de fin</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Grouper par</Form.Label>
                <Form.Select
                  value={filters.groupBy}
                  onChange={(e) => handleFilterChange('groupBy', e.target.value)}
                >
                  <option value="day">Jour</option>
                  <option value="week">Semaine</option>
                  <option value="month">Mois</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Utilisateur (ID)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Filtrer par utilisateur..."
                  value={filters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Modèle (ID)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Filtrer par modèle..."
                  value={filters.modelId}
                  onChange={(e) => handleFilterChange('modelId', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button variant="primary" onClick={handleApplyFilters} className="w-100">
                Appliquer
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <Row className="mb-4">
            <Col md={4}>
              <Card className="text-center">
                <Card.Body>
                  <Card.Title className="h2 text-primary">{totalRequests.toLocaleString()}</Card.Title>
                  <Card.Text className="text-muted">Requêtes totales</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center">
                <Card.Body>
                  <Card.Title className="h2 text-success">{totalTokens.toLocaleString()}</Card.Title>
                  <Card.Text className="text-muted">Tokens utilisés</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center">
                <Card.Body>
                  <Card.Title className="h2 text-info">{topModels.length}</Card.Title>
                  <Card.Text className="text-muted">Modèles actifs</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Usage Chart */}
          <Card className="mb-4">
            <Card.Body>
              <UsageChart data={usageData} title="Utilisation au fil du temps" />
            </Card.Body>
          </Card>

          {/* Top Models Table */}
          <Card>
            <Card.Header>Modèles les plus utilisés</Card.Header>
            <Card.Body>
              {topModels.length === 0 ? (
                <p className="text-muted text-center py-3">Aucune donnée disponible</p>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Modèle</th>
                      <th>Fournisseur</th>
                      <th>Requêtes</th>
                      <th>Tokens</th>
                      <th>Utilisateurs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topModels.map((model) => (
                      <tr key={model.id || model.model_id}>
                        <td>{model.name || model.code}</td>
                        <td>{model.provider}</td>
                        <td>{(model.request_count || model.count || 0).toLocaleString()}</td>
                        <td>{(model.total_tokens || model.tokens || 0).toLocaleString()}</td>
                        <td>{model.unique_users || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  )
}

export default Usage
